import Spinner from 'components/Common/Spinner';
import {
  SettingsDescription,
  SettingsMain,
  SettingsPageHeader,
  SettingsSectionTitle,
} from 'containers/SettingsPage/SettingsLayout';
import { useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import { selectCompany } from 'store/selectors/me/company';
import { shippingMethodAdded } from 'store/slices/getStarted';
import Icon from 'storybookConfig/stories/molecules/Icon';
import theme from 'storybookConfig/stories/theme';
import { createShippingMethod, getShippingMethods } from 'utils/api/shipping';
import { ACCOUNT_TYPE_SELLER } from 'utils/constants';
import { formatShippingLevel } from 'utils/shipping';
import ShippingMethod from './ShippingMethod';
import ShippingMethodForm from './ShippingMethodForm';
import PrimaryButton from 'storybookConfig/stories/molecules/Button/PrimaryButton';
import SecondaryButton from 'storybookConfig/stories/molecules/Button/SecondaryButton';

const { color } = theme;

const ShippingMethodFormArea = ({
  isLoading,
  fetchError,
  showCreateForm,
  setShowCreateForm,
  onCreateShippingMethod,
  onCreateSuccess,
  isSeller,
  shippingMethods,
  onDeleteSuccess,
  onUpdateSuccess,
  fastestShippingLevel,
}) => {
  if (isLoading)
    return (
      <div className="d-flex align-items-center">
        <Spinner color="primary" small />
        <p className="small mb-0 ml-3" style={{ color: color.bodyTextSecondary }}>
          Loading Shipping Methods
        </p>
      </div>
    );

  if (fetchError)
    return (
      <div className="d-flex align-items-center">
        <Icon name="triangle-alert" color="errorStrong" />
        <p className="small mb-0 ml-3" style={{ color: color.errorStrong }}>
          Error fetching shipping methods: {fetchError}
        </p>
      </div>
    );

  if (showCreateForm)
    return (
      <ShippingMethodForm
        onCancel={() => setShowCreateForm(false)}
        onSave={onCreateShippingMethod}
        onSaveSuccess={onCreateSuccess}
        isSeller={isSeller}
      />
    );

  if (shippingMethods.length === 0)
    return (
      <div className="mt-4">
        <p className="mb-3" style={{ color: color.bodyTextSecondary }}>
          You don&apos;t have any {isSeller ? 'supported' : 'required'} shipping methods configured.
          Add your first shipping method to get started.
        </p>
        <PrimaryButton size="small" $iconName="plus" onClick={() => setShowCreateForm(true)}>
          Add your first shipping method
        </PrimaryButton>
      </div>
    );

  return (
    <>
      {shippingMethods.map((method) => {
        return (
          <ShippingMethod
            key={method.id}
            shippingMethod={method}
            isSeller={isSeller}
            onDeleteSuccess={onDeleteSuccess}
            onUpdateSuccess={onUpdateSuccess}
          />
        );
      })}
      <p className="mt-4 mb-1 small" style={{ color: color.bodyTextSecondary }}>
        {isSeller
          ? `Partners will see your fastest option as `
          : 'Your fastest required option is '}
        <strong style={{ color: color.bodyTextPrimary }}>
          {formatShippingLevel(fastestShippingLevel)}
        </strong>
        . You can add slower options above.
      </p>
    </>
  );
};

const ShippingSettings = () => {
  const dispatch = useAppDispatch();
  const company = useAppSelector(selectCompany);
  const isSeller = company.commerceType === ACCOUNT_TYPE_SELLER;
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [shippingMethods, setShippingMethods] = useState([]);

  useEffect(() => {
    setIsLoading(true);
    getShippingMethods()
      .then(({ data }) => {
        if (data?.length === 0) {
          setShowCreateForm(true);
          return;
        }
        setShippingMethods(data.sort((a, b) => a.maxDays - b.maxDays));
      })
      .catch((err) => {
        setFetchError(err.message);
        console.error('getShippingMethods failed', err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const fastestShippingLevel = useMemo(() => {
    if (!shippingMethods?.length) return null;
    return Math.min(...shippingMethods.map((m) => m.maxDays));
  }, [shippingMethods]);

  const onCreateShippingMethod = (data) => createShippingMethod(data);

  const onCreateSuccess = ({ data }) => {
    setShippingMethods((prevState) => [...prevState, data].sort((a, b) => a.maxDays - b.maxDays));
    setShowCreateForm(false);
    dispatch(shippingMethodAdded());
  };

  const onDeleteSuccess = (methodId) => {
    setShippingMethods((prevState) => prevState.filter((method) => method.id !== methodId));
  };

  const onUpdateSuccess = ({ data }) => {
    setShippingMethods((prevState) =>
      prevState.map((method) => (method.id === data.id ? data : method))
    );
  };

  return (
    <SettingsMain>
      <SettingsPageHeader title="Shipping" />

      <hr style={{ margin: '24px 0' }} />

      <div className="d-flex justify-content-between align-items-center">
        {showCreateForm ? (
          <SettingsSectionTitle>
            {isSeller ? 'Add Supported Shipping Method' : 'Add Required Shipping Method'}
          </SettingsSectionTitle>
        ) : (
          <>
            <SettingsSectionTitle>
              {isSeller ? 'Supported Shipping Methods' : 'Required Shipping Methods'}
            </SettingsSectionTitle>

            <SecondaryButton
              size="small"
              $iconName="plus"
              onClick={() => {
                setShowCreateForm(true);
              }}
            >
              Add Shipping Method
            </SecondaryButton>
          </>
        )}
      </div>
      <SettingsDescription>
        {isSeller
          ? 'These are the shipping options you offer to your partners.'
          : 'These are the shipping options you expect partners to offer.'}
      </SettingsDescription>

      <ShippingMethodFormArea
        isLoading={isLoading}
        fetchError={fetchError}
        showCreateForm={showCreateForm}
        setShowCreateForm={setShowCreateForm}
        onCreateShippingMethod={onCreateShippingMethod}
        onCreateSuccess={onCreateSuccess}
        isSeller={isSeller}
        shippingMethods={shippingMethods}
        onDeleteSuccess={onDeleteSuccess}
        onUpdateSuccess={onUpdateSuccess}
        fastestShippingLevel={fastestShippingLevel}
      />
    </SettingsMain>
  );
};

export default ShippingSettings;
